import React, { useRef, useCallback, useState } from "react";
import { useTranslation } from 'react-i18next';
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import PageMeta from "@/components/common/PageMeta";
import { ActivitiesTable } from "@/features/activities/components/ActivitiesTable";
import { useActivities, useCreateActivity, useUpdateActivity, ACTIVITIES_PER_PAGE } from '@/features/activities/hooks/useActivities';
import { useSearchParams } from 'react-router-dom';
import Pagination from "@/components/common/Pagination";
import { ActivitiesParams, ActivityType, Activity } from '@/features/activities/types/activityTypes';
import { Loader } from "@/components/ui/loader/Loader";
import { TableColumnsFilter } from '@/components/ui/table/TableColumnsFilter';
import ActivitiesFilters from '@/features/activities/components/ActivitiesFilters';
import Button from '@/components/ui/button/Button';
import { Modal } from '@/components/ui/modal';
import ActivityForm from '@/features/activities/components/ActivityForm';
import { useModal } from '@/shared/hooks/useModal';

const ActivitiesPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) || 1;
  const { isOpen, openModal, closeModal } = useModal();
  const createActivity = useCreateActivity();
  const updateActivity = useUpdateActivity();
  const formRef = useRef<{ handleClose: () => void; isDirty: () => boolean } | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);

  // Получаем текущие фильтры из URL
  const currentFilters: Partial<ActivitiesParams> = {
    activity_type: (searchParams.get('activity_type') || undefined) as ActivityType | undefined,
    has_report: searchParams.get('has_report') === 'true' ? true : undefined,
    activity_start_date: searchParams.get('activity_start_date') || undefined,
    activity_end_date: searchParams.get('activity_end_date') || undefined
  };

  const { data, isLoading } = useActivities(currentPage, currentFilters);

  const handleFilterChange = (newFilters: Partial<ActivitiesParams>) => {
    const params = new URLSearchParams(searchParams);

    // Обновляем параметры фильтров
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.set(key, value.toString());
      } else {
        params.delete(key);
      }
    });

    // Сбрасываем страницу при изменении фильтров
    params.set('page', '1');

    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleModalClose = useCallback(() => {
    formRef.current?.handleClose();
  }, []);

  const handleFormClose = useCallback((hasUnsavedChanges?: boolean) => {
    if (hasUnsavedChanges === false) {
      closeModal();
    }
  }, [closeModal]);

  const handleCreateActivity = async (data: any) => {
    try {
      await createActivity.mutateAsync(data);
      closeModal();
    } catch (error) {
      throw error;
    }
  };

  const handleEditActivity = async (data: any) => {
    if (!selectedActivity) return;

    try {
      await updateActivity.mutateAsync({
        id: selectedActivity.id,
        data: {
          type: data.type,
          activity_date: data.activity_date,
          activity_time: data.activity_time,
          address: data.address,
          comment: data.comment,
        },
      });
      setSelectedActivity(null);
    } catch (error) {
      throw error;
    }
  };

  // Определяем столбцы для фильтра
  const TODO_COLUMNS = [
    { key: 'type', header: t('activities.columns.type'), field: 'type' },
    { key: 'car', header: t('activities.columns.car'), field: 'car' },
    { key: 'datetime', header: t('activities.columns.datetime'), field: 'datetime' },
    { key: 'address', header: t('activities.columns.address'), field: 'address' },
    { key: 'comment', header: t('activities.columns.comment'), field: 'comment' },
    { key: 'report', header: t('activities.columns.report'), field: 'report' },
    { key: 'actions', header: t('activities.columns.actions'), field: 'actions' },
  ];

  const TODO_TABLE_STORAGE_KEY = 'activities-table-columns';
  const DEFAULT_VISIBLE_COLUMNS = ['type', 'car', 'datetime', 'address', 'comment', 'report', 'actions'];

  return (
    <>
      <PageMeta
        title={t('activities.title')}
        description={t('activities.description')}
      />
      <PageBreadcrumb
        items={[
          { label: t('activities.breadcrumb'), path: '/activities' }
        ]}
        actions={
          <Button
            variant="primary"
            onClick={openModal}
          >
            {t('activities.create')}
          </Button>
        }
      />

      <div className="space-y-6">
        <TableColumnsFilter
          columns={TODO_COLUMNS}
          storageKey={TODO_TABLE_STORAGE_KEY}
          defaultColumns={DEFAULT_VISIBLE_COLUMNS}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center flex-1">
              <ActivitiesFilters
                currentFilters={currentFilters}
                onFilterChange={handleFilterChange}
              />
            </div>
            {!isLoading && (
              <div className="flex justify-start md:justify-end">
                <Pagination
                  totalItems={data?.total || 0}
                  itemsPerPage={ACTIVITIES_PER_PAGE}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px] bg-bg-secondary rounded-lg">
              <Loader />
            </div>
          ) : (
            <>
              <div className="bg-bg-secondary rounded-lg">
                <ActivitiesTable
                  activities={data?.data || []}
                  isLoading={false}
                  onEditActivity={handleEditActivity}
                />
              </div>
              <div className="flex justify-start md:justify-end mt-6">
                <Pagination
                  totalItems={data?.total || 0}
                  itemsPerPage={ACTIVITIES_PER_PAGE}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </TableColumnsFilter>
      </div>

      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        maxWidth="40rem"
      >
        <ActivityForm
          ref={formRef}
          onSubmit={handleCreateActivity}
          onClose={handleFormClose}
        />
      </Modal>
    </>
  );
};

export default ActivitiesPage;
